import { handleLogout } from "../config/axios";
import decryptData from "../decrypt";

/**
 * Service for handling user-related API calls
 * 
 * This service centralizes user authentication and agent code handling logic
 * to ensure consistent behavior across all pages of the application.
 */

/**
 * Fetches the user information from Keycloak
 * @returns User data including roles and agent codes
 */
export const fetchUserInfo = async () => {
  try {
    const response = await fetch("/nodeapi/keycloak-protected", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

      if (response.status !== 200 && response.status !== 201) {
          handleLogout(response, "/nodeapi/keycloak-protected", JSON.stringify(""));
         return;
       }
    
    const result = await response.json();
    
    if (!result || !result.user) {
      console.error("Invalid data structure from keycloak-protected");
      return {
        user: null,
        isSupervisor: false,
        agentCodes: [],
        agents: [],
        canExport: false
      };
    }
    
    const user = result.user;
    
    // Determine if user is supervisor
    const isSupervisor = user.roles && user.roles.includes("supervisor");
    
    // Extract agent codes from attributes
    let agentCodes: string[] = [];
    if (user.attributes?.as400_id && user.attributes.as400_id.length > 0) {
      // Handle case where agent has multiple codes in a single entry (comma-separated)
      if (typeof user.attributes.as400_id[0] === 'string' && user.attributes.as400_id[0].includes(',')) {
        agentCodes = user.attributes.as400_id[0].split(',').map(code => code.trim()).filter(Boolean);
      } else {
        agentCodes = user.attributes.as400_id.filter(Boolean);
      }
    }
    
    // Debug user agent codes
    console.log("Current user agent codes:", agentCodes);
    
    // Extract group members for supervisors
    let agents = [];
    if (isSupervisor && user.groupMembers) {
      agents = user.groupMembers.map((member: any) => {
        // Extract and parse multiple codes for agents too
        let agentAs400Ids = [];
        if (member.attributes?.as400_id) {
          // Check if there are multiple codes in a single entry
          if (typeof member.attributes.as400_id[0] === 'string' && member.attributes.as400_id[0].includes(',')) {
            agentAs400Ids = member.attributes.as400_id[0]
              .split(',')
              .map((code: string) => code.trim())
              .filter(Boolean); // Filter out empty strings
          } else {
            // Handle multiple entries in the array
            agentAs400Ids = member.attributes.as400_id.filter(Boolean);
          }
        }
        
        // Debug agent's codes
        console.log(`Agent ${member.firstName} ${member.lastName} codes:`, agentAs400Ids);
        
        return {
          id: member.id,
          db2_user_id: agentAs400Ids.join(','), // For backward compatibility
          all_codes: agentAs400Ids,
          first_name: member.firstName || "",
          last_name: member.lastName || "",
        };
      });
      
      // Debug all collected agents
      console.log("All agents with their codes:", 
        agents.map(a => ({ name: `${a.first_name} ${a.last_name}`, codes: a.all_codes })));
    }
    
    // Extract export_anagrafica attribute (controls CSV/PDF export visibility in AnagraficaClienti)
    const canExport = !!(user.attributes?.export_anagrafica && user.attributes.export_anagrafica[0] === 'true');

    return {
      user,
      isSupervisor,
      agentCodes,
      agents,
      canExport
    };
  } catch (error) {
    console.error("Error fetching user info:", error);
    return {
      user: null,
      isSupervisor: false,
      agentCodes: [],
      agents: [],
      canExport: false
    };
  }
};

/**
 * Collects all agent codes for API requests, including:
 * - Current user's codes
 * - Codes of all subordinate agents (for supervisors)
 * 
 * @returns Array of unique agent codes
 */
export const getAllAgentCodes = async (): Promise<string[]> => {
  try {
    const { user, isSupervisor, agentCodes, agents } = await fetchUserInfo();
    
    if (!user) {
      console.error("No user data available");
      return [];
    }
    
    // Start with current user's codes
    let allCodes = [...agentCodes];
    
    // For supervisors, add codes from all subordinate agents
    if (isSupervisor && agents.length > 0) {
      agents.forEach(agent => {
        if (agent.all_codes && agent.all_codes.length > 0) {
          allCodes = [...allCodes, ...agent.all_codes];
        }
      });
    }
    
    // Remove duplicates and filter out any empty values
    const uniqueCodes = [...new Set(allCodes)].filter(Boolean);
    console.log("getAllAgentCodes - All unique agent codes:", uniqueCodes);
    
    return uniqueCodes;
  } catch (error) {
    console.error("Error in getAllAgentCodes:", error);
    return [];
  }
};

/**
 * Fetches clients/users for the specified agent codes
 * @param agentCodes - Array of agent codes to fetch clients for
 * @returns Array of clients with their information
 */
export const fetchClientsByAgentCodes = async (agentCodes: string[] = []) => {
  try {
    // If no codes provided, get all relevant codes
    const codesToUse = agentCodes.length > 0 ? agentCodes : await getAllAgentCodes();
    
    if (!codesToUse || codesToUse.length === 0) {
      console.error("No agent codes available for fetchClientsByAgentCodes");
      return [];
    }
    
    // Ensure all agent codes are properly processed (no comma-separated values)
    const processedCodes = codesToUse.flatMap(code => {
      if (typeof code === 'string' && code.includes(',')) {
        return code.split(',').map(c => c.trim()).filter(Boolean);
      }
      return code ? [code] : [];
    });
    
    console.log("Processed agent codes for utenti request:", processedCodes);
    
    const requestBody = {
      agenti: processedCodes,
      page: 1,
      pageSize: 1000,
    };
    
    const response = await fetch("/nodeapi/utenti", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(requestBody),
    });

     if (response.status !== 200 && response.status !== 201) {
          handleLogout(response, "/nodeapi/utenti", JSON.stringify(requestBody));
         return;
       }
    
  
    
    const result = await response.json();
    
    // Handle encrypted response
    if (result.encryptedData && result.encryptedKey && result.iv) {
      const decrypted = decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      
      if (!decrypted) {
        console.error("Failed to decrypt data");
        return [];
      }
      
      try {
        const decryptedJson = JSON.parse(decrypted);
        return decryptedJson.data || [];
      } catch (e) {
        console.error("Error parsing decrypted JSON:", e);
        return [];
      }
    }
    
    // Handle non-encrypted response (fallback)
    return result.data || [];
  } catch (error) {
    console.error("Error fetching clients by agent codes:", error);
    return [];
  }
};

/**
 * Creates an array of formatted agents for dropdown selection
 * from raw agent data
 * 
 * @param agents Array of agent objects
 * @returns Formatted agents for UI selection
 */
export const formatAgentsForUI = (agents, user = null) => {
  if (!agents || !Array.isArray(agents)) return [];
  
  return agents.map(agent => ({
    value: JSON.stringify(agent.all_codes),
    label: `${agent.first_name} ${agent.last_name}`,
    db2_user_id: JSON.stringify(agent.all_codes),
    raw_codes: agent.all_codes
  }));
};

/**
 * Determines if a string contains multiple agent codes (comma-separated)
 * @param value - String value to check
 * @returns boolean - true if value contains multiple codes
 */
export const hasMultipleCodes = (value: string): boolean => {
  return typeof value === 'string' && value.includes(',');
};

/**
 * Splits a comma-separated string of agent codes into an array
 * @param codes - Comma-separated string of agent codes
 * @returns Array of trimmed agent codes
 */
export const splitAgentCodes = (codes: string): string[] => {
  if (!codes) return [];
  
  if (hasMultipleCodes(codes)) {
    return codes.split(',').map(code => code.trim()).filter(Boolean);
  }
  
  return [codes];
};

/**
 * Processes agent code values from different formats
 * @param value - Value to process (string, JSON string, etc.)
 * @returns Array of agent codes
 */
export const processAgentCodeValue = (value: any): string[] => {
  if (!value) return [];
  
  // Handle array
  if (Array.isArray(value)) {
    // Process each element to handle any comma-separated values within the array
    return value.flatMap(code => {
      if (typeof code === 'string' && code.includes(',')) {
        return code.split(',').map(c => c.trim()).filter(Boolean);
      }
      return code ? [code] : [];
    });
  }
  
  // Handle JSON string
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        // Process each element of the parsed array
        return parsed.flatMap(code => {
          if (typeof code === 'string' && code.includes(',')) {
            return code.split(',').map(c => c.trim()).filter(Boolean);
          }
          return code ? [code] : [];
        });
      }
    } catch (e) {
      // Not a JSON string, continue with other processing
    }
    
    // Handle comma-separated string
    if (hasMultipleCodes(value)) {
      return splitAgentCodes(value);
    }
    
    // Single value
    return value ? [value] : [];
  }
  
  return [];
};

/**
 * Prepares agent codes for API requests
 * Makes sure all agent codes are properly formatted for the API
 * 
 * @param agentCodes - Agent codes to process
 * @returns Properly formatted agent codes for API
 */
export const prepareAgentCodesForAPI = (agentCodes: any): string[] => {
  if (!agentCodes) return [];
  
  // If we already have an array of codes
  if (Array.isArray(agentCodes)) {
    // Process each element to handle any comma-separated values
    return agentCodes.flatMap(code => {
      if (typeof code === 'string' && code.includes(',')) {
        return code.split(',').map(c => c.trim()).filter(Boolean);
      }
      return code ? [code] : [];
    });
  }
  
  // If we have an object with value property (e.g., from Select component)
  if (agentCodes && typeof agentCodes === 'object' && 'value' in agentCodes) {
    return processAgentCodeValue(agentCodes.value);
  }
  
  // If we have a comma-separated string
  if (typeof agentCodes === 'string' && agentCodes.includes(',')) {
    return agentCodes.split(',').map(c => c.trim()).filter(Boolean);
  }
  
  // Default case: single value
  return agentCodes ? [agentCodes].filter(Boolean) : [];
};

/**
 * Prepares agent codes for any usage
 * @param agentCodes - Agent codes to process, can be in different formats
 * @returns Array of processed agent codes
 */
export const prepareAgentCodes = (agentCodes: any): string[] => {
  return processAgentCodeValue(agentCodes);
};

/**
 * Formats agent codes for API requests - if there's a single code, return as a string,
 * otherwise format as an array for the payload
 * @param agentCodes - Array of agent codes
 * @returns Object with codice_agente or agenti property
 */
export const formatAgentCodesForRequest = (agentCodes: string[] | string) => {
  if (!agentCodes) {
    return {};
  }
  
  // Make sure we have an array of individual codes
  let processedCodes: string[] = [];
  
  if (Array.isArray(agentCodes)) {
    // Process each element to handle any comma-separated values
    processedCodes = agentCodes.flatMap(code => {
      if (typeof code === 'string' && code.includes(',')) {
        return code.split(',').map(c => c.trim()).filter(Boolean);
      }
      return code ? [code] : [];
    });
  } else if (typeof agentCodes === 'string') {
    // Handle comma-separated string
    if (agentCodes.includes(',')) {
      processedCodes = agentCodes.split(',').map(c => c.trim()).filter(Boolean);
    } else {
      // Single code
      processedCodes = [agentCodes];
    }
  }
  
  if (processedCodes.length === 0) {
    return {};
  }
  
  // Always use the 'agenti' format for consistency
  return { agenti: processedCodes };
};
